/**
 * SportCity (MA-72): de clubs en de plaatsen waar er een bij kan.
 *
 * De vuistregel is één club per 40.000 inwoners. De API geeft de ruwe cijfers:
 * inwoners, clubs en m² per plaats, en de dichtstbijzijnde club. Norm en tekort
 * rekent de web-app zelf uit, zodat je de norm kunt verschuiven zonder een
 * nieuwe vraag aan de server.
 *
 * Bronnen: de clubs van sportcity.nl, de m² uit de BAG (gebruiksoppervlakte van
 * het verblijfsobject op het clubadres), de inwoners van CBS via AlleCijfers, en
 * de coördinaten van de PDOK Locatieserver.
 */
import { z } from 'zod';
import type { Punt } from './common';

/** Eén club per zoveel inwoners. */
export const SPORTCITY_NORM_STANDAARD = 40_000;
export const SPORTCITY_NORM_MIN = 10_000;
export const SPORTCITY_NORM_MAX = 150_000;

/**
 * Hoe zeker de m² zijn. `ok`: het BAG-object past bij een sportclub.
 * `controleren`: bruikbaar, maar er is een aanname gedaan. `onbekend`: niet uit
 * de BAG af te leiden; telt niet mee in de m².
 */
export const CLUB_STATUSSEN = ['ok', 'controleren', 'onbekend'] as const;
export const clubStatusSchema = z.enum(CLUB_STATUSSEN);
export type ClubStatus = z.infer<typeof clubStatusSchema>;

export const CLUB_STATUS_LABELS: Record<ClubStatus, string> = {
  ok: 'OK',
  controleren: 'Controleren',
  onbekend: 'Onbekend',
};

export const sportcityClubSchema = z.object({
  id: z.string(),
  naam: z.string(),
  adres: z.string(),
  woonplaats: z.string(),
  gemeente: z.string(),
  /** CBS-code van de gemeente (`GM0363`), voor de wijkcijfers (MA-73). */
  gemeentecode: z.string().regex(/^GM\d{4}$/),
  lat: z.number(),
  lng: z.number(),
  /** Gebruiksoppervlakte op het clubadres volgens de BAG. */
  m2Bag: z.number().int().nullable(),
  /** De m² waarmee gerekend wordt; null als ze onbekend zijn. */
  m2: z.number().int().nullable(),
  bagObjecten: z.number().int(),
  gebruiksdoel: z.string().nullable(),
  bouwjaar: z.number().int().nullable(),
  status: clubStatusSchema,
  toelichting: z.string().nullable(),
});
export type SportcityClub = z.infer<typeof sportcityClubSchema>;

/**
 * `woonplaats`: een woonplaats van 40.000+, of een kleinere met een club.
 * `gemeente`: een gemeente van 40.000+ zonder club, waarvan de inwoners over
 * meerdere kernen verspreid zijn - de grootste kern haalt de 40.000 niet.
 */
export const PLAATS_SOORTEN = ['woonplaats', 'gemeente'] as const;
export const plaatsSoortSchema = z.enum(PLAATS_SOORTEN);
export type PlaatsSoort = z.infer<typeof plaatsSoortSchema>;

export const sportcityPlaatsSchema = z.object({
  id: z.string(),
  naam: z.string(),
  gemeente: z.string(),
  gemeentecode: z.string().regex(/^GM\d{4}$/),
  soort: plaatsSoortSchema,
  inwoners: z.number().int(),
  /** Bij een gemeente: de grootste kern en zijn inwoners, als die er is. */
  kern: z.string().nullable(),
  kernInwoners: z.number().int().nullable(),
  /** Het midden van de woonplaats, of van de kern bij een gemeente. */
  lat: z.number(),
  lng: z.number(),
  clubs: z.number().int(),
  /** Opgeteld over de clubs waarvan de m² bekend zijn. */
  m2: z.number().int(),
  clubsZonderM2: z.number().int(),
  dichtstbij: z.object({ clubId: z.string(), naam: z.string(), km: z.number() }).nullable(),
});
export type SportcityPlaats = z.infer<typeof sportcityPlaatsSchema>;

export const sportcityOverzichtSchema = z.object({
  /** Waar de cijfers vandaan komen, in één regel per bron. */
  bronnen: z.array(z.string()),
  clubs: z.array(sportcityClubSchema),
  plaatsen: z.array(sportcityPlaatsSchema),
});
export type SportcityOverzicht = z.infer<typeof sportcityOverzichtSchema>;

/** Hoeveel clubs een plaats volgens de norm draagt: naar beneden afgerond. */
export function sportcityNorm(inwoners: number, perClub = SPORTCITY_NORM_STANDAARD): number {
  if (perClub <= 0) return 0;
  return Math.floor(inwoners / perClub);
}

/** Hoeveel clubs er volgens de norm bij kunnen; nooit negatief. */
export function sportcityTekort(
  plaats: Pick<SportcityPlaats, 'inwoners' | 'clubs'>,
  perClub = SPORTCITY_NORM_STANDAARD,
): number {
  return Math.max(0, sportcityNorm(plaats.inwoners, perClub) - plaats.clubs);
}

const AARDSTRAAL_KM = 6371.0088;

/** Hemelsbreed, in kilometers. */
export function afstandKm(a: Punt, b: Punt): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * AARDSTRAAL_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Een cirkel om een punt als ring van [lng, lat], gesloten (het eerste punt
 * staat ook achteraan), voor een GeoJSON-polygoon op de kaart.
 */
export function cirkelRing(midden: Punt, straalKm: number, stappen = 64): [number, number][] {
  const rad = Math.PI / 180;
  const hoek = straalKm / AARDSTRAAL_KM;
  const lat1 = midden.lat * rad;
  const lng1 = midden.lng * rad;
  const ring: [number, number][] = [];
  for (let i = 0; i <= stappen; i++) {
    const richting = ((i % stappen) / stappen) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(hoek) + Math.cos(lat1) * Math.sin(hoek) * Math.cos(richting),
    );
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(richting) * Math.sin(hoek) * Math.cos(lat1),
        Math.cos(hoek) - Math.sin(lat1) * Math.sin(lat2),
      );
    ring.push([lng2 / rad, lat2 / rad]);
  }
  return ring;
}
